import React from 'react';
import styled from 'styled-components';
import { AlertTriangle, CheckCircle2, Clock3, MinusCircle, XCircle } from 'lucide-react';

/**
 * One state vocabulary for the ABF workspace (FRD FR-0.1): every state is an
 * icon plus a word, never colour alone.
 */
export type StatusTone = 'done' | 'critical' | 'warning' | 'progress' | 'neutral';

const TONES: Record<StatusTone, { fg: string; bg: string; border: string; Icon: typeof CheckCircle2 }> = {
  done: { fg: '#6ee7b7', bg: 'rgba(6, 78, 59, 0.28)', border: 'rgba(52, 211, 153, 0.4)', Icon: CheckCircle2 },
  critical: { fg: '#fca5a5', bg: 'rgba(127, 29, 29, 0.3)', border: 'rgba(248, 113, 113, 0.45)', Icon: XCircle },
  warning: { fg: '#fcd34d', bg: 'rgba(120, 53, 15, 0.3)', border: 'rgba(251, 191, 36, 0.42)', Icon: AlertTriangle },
  progress: { fg: '#7dd3fc', bg: 'rgba(12, 74, 110, 0.3)', border: 'rgba(56, 189, 248, 0.42)', Icon: Clock3 },
  neutral: { fg: '#cbd5e1', bg: 'rgba(30, 41, 59, 0.5)', border: 'rgba(148, 163, 184, 0.3)', Icon: MinusCircle },
};

const Chip = styled.span<{ $tone: StatusTone; $size: 'sm' | 'md' }>`
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 0.3rem;
  padding: ${p => p.$size === 'md' ? '0.32rem 0.6rem' : '0.18rem 0.45rem'};
  border: 1px solid ${p => TONES[p.$tone].border};
  border-radius: 999px;
  background: ${p => TONES[p.$tone].bg};
  color: ${p => TONES[p.$tone].fg};
  font-size: ${p => p.$size === 'md' ? '0.72rem' : '0.62rem'};
  font-weight: 800;
  letter-spacing: 0.02em;
  white-space: nowrap;
`;

export const StatusChip: React.FC<{ tone: StatusTone; label: string; size?: 'sm' | 'md' }> = ({
  tone,
  label,
  size = 'sm',
}) => {
  const { Icon } = TONES[tone];
  return (
    <Chip $tone={tone} $size={size}>
      <Icon size={size === 'md' ? 13 : 11} aria-hidden="true" />
      {label}
    </Chip>
  );
};

export default StatusChip;
